<template>
  <!-- Teleported to <body>: `position: fixed` resolves against the nearest
       transformed ancestor, and this site's pages carry a permanently
       translated track. -->
  <Teleport to="body">
    <Transition name="ask-fade">
      <div v-if="open" class="ask-overlay" @click="close">
        <div class="ask" role="dialog" aria-modal="true" aria-label="Request the résumé" @click.stop>
          <button type="button" class="ask__close" aria-label="Close" @click="close">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div class="ask__mark">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="8" y1="13" x2="16" y2="13" />
              <line x1="8" y1="17" x2="13" y2="17" />
            </svg>
          </div>

          <template v-if="sent">
            <h3 class="ask__title">Asked</h3>
            <p class="ask__body">
              I have your request. I will send it over shortly.
            </p>
            <button type="button" class="ask__done" @click="close">Close</button>
          </template>

          <template v-else>
            <h3 class="ask__title">Not available right now</h3>
            <p class="ask__body">
              The résumé is not up at the moment. Check back later, or leave your
              details and I will send it across.
            </p>

            <!-- Registration does NOT come from this markup. Netlify parses HTML
                 in the publish directory at deploy time, so the form is declared
                 in public/__forms.html as well. Keep the two field lists in
                 step: Netlify drops fields it has not seen declared. -->
            <form
              class="ask__form"
              name="resume-request"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              @submit.prevent="send"
            >
              <input type="hidden" name="form-name" value="resume-request">

              <p class="ask__trap" aria-hidden="true">
                <label>
                  Leave this field empty
                  <input v-model="form.botField" name="bot-field" tabindex="-1" autocomplete="off">
                </label>
              </p>

              <label class="ask__field">
                <span>Name</span>
                <input v-model="form.name" name="name" type="text" required maxlength="80" :class="{ 'has-error': errors.name }">
              </label>

              <label class="ask__field">
                <span>Email</span>
                <input v-model="form.email" name="email" type="email" required maxlength="120" inputmode="email" :class="{ 'has-error': errors.email }">
              </label>

              <label class="ask__field">
                <span>What it is for <em>optional</em></span>
                <textarea v-model="form.reason" name="reason" rows="3" maxlength="400"/>
              </label>

              <p v-if="failed" class="ask__error selectable">
                That did not send. You can email me at {{ personalInfo.email }}.
              </p>

              <button type="submit" class="ask__submit" :disabled="sending">
                {{ sending ? 'Sending…' : 'Send request' }}
              </button>
            </form>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { reactive, ref, watch } from 'vue';
import { personalInfo } from '~/data/content';

const props = defineProps({
  open: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const form = reactive({ name: '', email: '', reason: '', botField: '' });
const errors = reactive({ name: '', email: '' });
const sending = ref(false);
const sent = ref(false);
const failed = ref(false);

// Reset on open rather than on close, so the confirmation stays on screen
// while the panel fades out instead of flashing back to the form.
watch(() => props.open, (isOpen) => {
  if (!isOpen) return;
  Object.assign(form, { name: '', email: '', reason: '', botField: '' });
  Object.assign(errors, { name: '', email: '' });
  sending.value = false;
  sent.value = false;
  failed.value = false;
});

function close() {
  emit('close');
}

function validate() {
  errors.name = form.name.trim() ? '' : 'Name is required';
  errors.email = !form.email.trim()
    ? 'Email is required'
    : /\S+@\S+\.\S+/.test(form.email) ? '' : 'That email does not look right';
  return !errors.name && !errors.email;
}

// Netlify expects a URL-encoded body, not JSON or FormData.
async function send() {
  if (!validate()) return;

  // A bot filled the honeypot. Answered as though it worked, so it learns
  // nothing, and nothing is sent.
  if (form.botField) {
    sent.value = true;
    return;
  }

  sending.value = true;
  failed.value = false;

  try {
    const body = new URLSearchParams({
      'form-name': 'resume-request',
      name: form.name.trim(),
      email: form.email.trim(),
      reason: form.reason.trim(),
    });

    const response = await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!response.ok) throw new Error(String(response.status));
    // The form above is what reaches the inbox. This is what reaches the
    // list, so every address the site is given lives in one table whatever
    // door it came through. Deliberately not awaited into the outcome: the
    // request has been made and the reader should be told so, whether or
    // not the list write lands.
    fetch('/.netlify/functions/subscribe', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: form.email.trim(), source: 'resume' }),
    }).catch(() => {});

    sent.value = true;
  } catch {
    failed.value = true;
  } finally {
    sending.value = false;
  }
}
</script>

<style lang="scss" scoped>
.ask-overlay {
  position: fixed;
  inset: 0;
  z-index: 3200;
  display: flex;
  justify-content: center;
  // Centred by the card's auto margin, so a card taller than the screen can
  // still be scrolled to its top.
  overflow-y: auto;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.5);
}

.ask {
  position: relative;
  margin: auto;
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem 1.75rem 1.5rem;
  border-radius: 18px;
  border: 1px solid rgba(var(--border-rgb), 0.4);
  background: var(--background);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.ask__close {
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: rgba(var(--muted-rgb), 0.12);
  color: var(--text);
  cursor: pointer;

  &:hover { background: rgba(var(--muted-rgb), 0.22); }
}

.ask__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: 0 auto 0.9rem;
  border-radius: 50%;
  background: rgba(var(--accent-rgb), 0.1);
  color: var(--accent);
}

.ask__title {
  margin: 0 0 0.55rem;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
}

.ask__body {
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.65;
  color: var(--secondary);
}

.ask__form {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: 1.25rem;
  text-align: left;
}

.ask__trap {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.ask__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  > span {
    font-size: 0.74rem;
    font-weight: 600;
    color: var(--secondary);

    em {
      font-style: normal;
      font-weight: 400;
      color: var(--muted);
    }
  }

  input,
  textarea {
    width: 100%;
    padding: 0.55rem 0.7rem;
    border: 1px solid rgba(var(--border-rgb), 0.45);
    border-radius: 10px;
    background: transparent;
    font: inherit;
    // Under 16px a phone zooms the page in when the field is focused.
    font-size: 16px;
    color: var(--text);
    resize: vertical;

    &:focus {
      outline: none;
      border-color: rgba(var(--accent-rgb), 0.65);
    }

    &.has-error { border-color: rgba(220, 38, 38, 0.6); }
  }
}

.ask__error {
  margin: 0;
  font-size: 0.76rem;
  color: #dc2626;
}

.ask__submit,
.ask__done {
  width: 100%;
  margin-top: 0.35rem;
  padding: 0.6rem 1.1rem;
  border-radius: 999px;
  border: none;
  background: var(--accent);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  color: #fff;
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: default; }
}

.ask__done {
  margin-top: 1.25rem;
  background: rgba(var(--muted-rgb), 0.14);
  color: var(--text);
}

.ask-fade-enter-active,
.ask-fade-leave-active {
  transition: opacity 0.22s ease;

  .ask { transition: transform 0.22s ease; }
}

.ask-fade-enter-from,
.ask-fade-leave-to {
  opacity: 0;

  .ask { transform: translateY(8px) scale(0.97); }
}
</style>
